import React, { useCallback, useState } from 'react';
import { IonContent, IonIcon } from '@ionic/react';
import { Container } from '@mindly/ui-components';
import { closeCircleOutline, pauseCircleOutline } from 'ionicons/icons';
import { ISubscription } from '../../../redux/subscriptions/types';
import { JSX } from '@ionic/core/components';
import { FullWidthModal } from '../SubscriptionControl.styled';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSubscriptions } from '../../../redux/subscriptions/thunk';
import { userSelector } from '../../../redux/user/selector';
import { patchSubscription } from '../../../api/subscription';
import { selectRoomDataBySenderId } from '../../../redux/chatRooms/selector';
import { SystemMessagesType, UserType } from '../../types';
import useAgoraRtm from '../../../context/AgoraContext/AgoraContext';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatSystemMessage } from '../../../context/AgoraContext/formatSystemMessage';
import useLangs from '../../../context/LangsContext';
import EmailSystem from '../../../api/emails';
import FirstStep from './Steps/FirstStep';
import LoaderStep from './Steps/LoaderStep';
import FinishStep from './Steps/FinishStep';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { CancelSubscriptionModalWrapper } from '../CancelSubscriptionModal/CancelSubscriptionModal.style';
import HelpButton from '../../../components/HelpButton';
import { SUBSCRIPTION_PAUSE } from '../../../intercom-articles';
import useToast from '../../../context/ToastContext/ToastContext';
import { useAnalytics } from 'src/shared';

type PauseModalProps = {
  onDidDismiss: () => void;
  subscription?: ISubscription | null;
} & JSX.IonModal;

const PauseModal: React.FC<PauseModalProps> = ({ subscription, ...props }) => {
  const intl = useIntl();
  const { locale } = useLangs();
  const dispatch = useDispatch();
  const { trackPredefinedEvent } = useAnalytics();

  const { showToast } = useToast();

  const [step, setStep] = useState(1);

  const user = useSelector(userSelector);
  const roomData = useSelector(selectRoomDataBySenderId(subscription?.therapist_id ?? ''));

  const { sendSystemMessage } = useAgoraRtm();

  const pauseSubscriptionHandler = useCallback(async () => {
    try {
      trackPredefinedEvent('SUBSCRIPTION_PAUSE_CONFIRMATION_MODAL_CLICKED', {
        subscription_id: subscription?.id,
        specialist_id: subscription?.therapist_id,
      });

      if (!subscription?.id)
        throw new Error(
          intl.formatMessage({
            id: 'subscription.error.data_not_loaded_yet',
            defaultMessage: 'Схоже дані не завантажились.',
          })
        );

      setStep(2);
      await patchSubscription(
        {
          pause_collection: true,
        },
        user?.id,
        subscription?.id
      );
      await dispatch(fetchSubscriptions(user?.id));

      sendSystemMessage({
        user: user as Omit<UserType, 'id'> & { id: string },
        specialistId: subscription?.therapist_id,
        message: formatSystemMessage(
          SystemMessagesType.subscription_paused,
          {
            therapistName: `${subscription.therapist.first_name} ${subscription.therapist.last_name[0]}`,
          },
          intl
        ),
        notifyMessage: intl.formatMessage({
          id: 'subscription.notify_message.pause',
          defaultMessage: 'Призупинив(ла) підписку',
        }),
        roomData,
        locale,
      });
      EmailSystem.sendSubscription('paused', subscription.id);

      setStep(3);
    } catch (error) {
      setStep(1);
      showToast({ error });
    }
  }, [user, subscription]);

  const handleCloseModal = useCallback(() => {
    props.onDidDismiss();
    setStep(1);
  }, [props.onDidDismiss]);

  return (
    <FullWidthModal
      {...props}
      onIonModalDidDismiss={() => {
        setStep(1);
      }}
    >
      {step !== 2 && (
        <PageHeader backChevron={step === 1} backChevronAction={handleCloseModal} showAvatar={step !== 3}>
          <IonIcon style={{ fontSize: '27px' }} icon={closeCircleOutline} onClick={handleCloseModal} />
        </PageHeader>
      )}
      <IonContent>
        <Container
          style={{
            paddingTop: 0,
            marginTop: '-15px',
            justifyContent: 'center',
          }}
        >
          <CancelSubscriptionModalWrapper>
            {step === 1 && <IonIcon icon={pauseCircleOutline} className={'pause-icon'} />}
            {(() => {
              switch (step) {
                case 1:
                  return <FirstStep handleNextStep={pauseSubscriptionHandler} />;
                case 2:
                  return <LoaderStep />;
                case 3:
                  return <FinishStep handleClose={handleCloseModal} />;
              }
            })()}
          </CancelSubscriptionModalWrapper>
          {step === 1 && (
            <HelpButton articleId={SUBSCRIPTION_PAUSE} style={{ position: 'fixed', bottom: '50px' }}>
              <FormattedMessage id={'subscription.pause.help.btn'} defaultMessage={'Як це працює?'} />
            </HelpButton>
          )}
        </Container>
      </IonContent>
    </FullWidthModal>
  );
};

export default PauseModal;
